import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { leaveOverview } from '../utils/leaveAlerts';

// "Who's away" strip for the Dashboard: everyone on approved leave today, then every booking
// starting in the next 14 days. Same digest as the 6am leave-alerts push (utils/leaveAlerts.js).
// Added after Pablo's approved day off (26 Oct 2026) went unnoticed until someone opened the
// calendar.
const sydneyToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
const fmtDay = (iso) => new Date(iso + 'T12:00:00Z').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const when = (n) => (n === 1 ? 'tomorrow' : `in ${n} days`);
const span = (r) => (r.days === 1 ? fmtDay(r.startDate) : `${fmtDay(r.startDate)} → ${fmtDay(r.endDate)}`);

function Person({ r, children }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: '.8rem', lineHeight: 1.5 }}>
      <strong>{r.name}</strong>
      {r.teams.length > 0 && <span style={{ color: 'var(--hp-dim)', fontSize: '.72rem' }}>{r.teams.join(' / ')}</span>}
      <span style={{ color: 'var(--hp-dim)' }}>{children}</span>
    </div>
  );
}

export default function LeaveTodayBanner({ entries }) {
  const navigate = useNavigate();
  const ov = leaveOverview(entries, sydneyToday(), { ahead: 14 });
  if (!ov.away.length && !ov.upcoming.length) return null;
  const hot = ov.away.length > 0 || ov.upcoming.some((r) => r.startsIn <= 1);

  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 8,
      background: hot ? 'rgba(239,68,68,0.07)' : 'rgba(59,130,246,0.07)',
      border: `1px solid ${hot ? 'rgba(239,68,68,0.35)' : 'rgba(59,130,246,0.3)'}`,
      borderRadius: 8, padding: '10px 16px',
    }}>
      <CalendarDays size={16} color={hot ? 'var(--hp-red)' : 'var(--hp-blue)'} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 8 }}>
        {ov.away.length > 0 && (
          <div>
            <div style={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--hp-red)' }}>
              On leave today ({ov.away.length})
            </div>
            {ov.away.map((r) => (
              <Person key={r.id} r={r}>{r.type} · {r.endDate === ov.today ? 'back next shift' : `until ${fmtDay(r.endDate)}`}</Person>
            ))}
          </div>
        )}
        {ov.upcoming.length > 0 && (
          <div>
            <div style={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>
              Coming up — next 14 days
            </div>
            {ov.upcoming.map((r) => (
              <Person key={r.id} r={r}>
                <span style={{ color: r.startsIn <= 1 ? 'var(--hp-red)' : undefined, fontWeight: r.startsIn <= 1 ? 600 : undefined }}>{when(r.startsIn)}</span>
                {' '}· {r.type} · {span(r)}
              </Person>
            ))}
          </div>
        )}
      </div>
      <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0, fontSize: '.75rem' }} onClick={() => navigate('/roster')}>
        <CalendarDays size={13} /> Leave Calendar
      </button>
    </div>
  );
}
